import { cva } from "class-variance-authority";
import type { HTMLAttributes } from "react";
import { cn } from "@/lib/utils";

export type DividerProps = Omit<HTMLAttributes<HTMLDivElement>, "className"> & {
  orientation?: "horizontal" | "vertical";
  /** Ignored when `subheader` or `centeredText` is set. @default "full-width" */
  variant?: "full-width" | "inset" | "middle-inset";
  /** Horizontal only. Renders a text label below the line.*/
  subheader?: string;
  /**
   * Horizontal full-width only. Splits the line into two equal segments with
   * the text centred between them
   */
  centeredText?: string;
  /** @default "default" */
  emphasis?: "default" | "high";
};

const lineColor = {
  default: "border-outline-subtle",
  high: "border-outline opacity-60",
} as const;

const INSET_CLASS = {
  horizontal: {
    "full-width": "",
    inset: "pl-(--spacing-16)",
    "middle-inset": "px-(--spacing-16)",
  },
  vertical: {
    "full-width": "",
    inset: "pt-(--spacing-16)",
    "middle-inset": "py-(--spacing-16)",
  },
} as const;

const textStyles = cva([
  "text-text-subtext",
  "font-(family-name:--font-family-body)",
  "font-(number:--font-weight-medium)",
  "leading-(--font-line-height-20)",
  "tracking-(--font-letter-spacing-default)",
  "whitespace-nowrap",
]);

/**
 * A 1px visual separator for grouping and distinguishing sections of content.
 * Supports horizontal and vertical orientations, inset configurations,
 * subheader labels, and centred text.
 *
 * @example
 * <Divider />
 * <Divider orientation="vertical" />
 * <Divider variant="inset" />
 * <Divider subheader="Section" />
 * <Divider centeredText="or" />
 * <Divider emphasis="high" />
 */
export function Divider({
  orientation = "horizontal",
  variant = "full-width",
  subheader,
  centeredText,
  emphasis = "default",
  ...props
}: DividerProps) {
  const isVertical = orientation === "vertical";
  const color = lineColor[emphasis];
  const lineBorder = isVertical ? "border-l" : "border-t";

  const insetClass = INSET_CLASS[orientation][variant];

  if (!isVertical && centeredText) {
    return (
      <div {...props} className="flex items-center gap-(--spacing-6)">
        <hr
          aria-orientation="horizontal"
          className={cn("grow", lineBorder, color)}
        />
        <span
          className={cn(
            textStyles(),
            "shrink-0",
            "text-(length:--font-size-body-small)",
          )}
        >
          {centeredText}
        </span>
        <hr
          aria-orientation="horizontal"
          className={cn("grow", lineBorder, color)}
        />
      </div>
    );
  }
  if (!isVertical && subheader) {
    return (
      <div
        {...props}
        className="flex flex-col gap-(--spacing-4) px-(--spacing-16)"
      >
        <hr
          aria-orientation="horizontal"
          className={cn("w-full", lineBorder, color)}
        />
        <span
          className={cn(
            textStyles(),
            "shrink-0",
            "text-(length:--font-size-body-medium)",
          )}
        >
          {subheader}
        </span>
      </div>
    );
  }

  return (
    <div
      {...props}
      className={cn("flex", isVertical ? "flex-col" : "flex-row", insetClass)}
    >
      <hr
        aria-orientation={orientation}
        className={cn("grow", lineBorder, color)}
      />
    </div>
  );
}
